<mic-modal #modalRef (close)="onModalClosed()"></mic-modal>

<ng-template #modalContent>
  <form [formGroup]="lineWOForm">
    <div class="flex flex-col mt-0 mb-1 gap-1">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="mt-0 mb-0">
            {{ isEditMode ? `${translationService.translate('editText')} ${translationService.translate('workOrderText')}` : translationService.translate('addLineButtonText') }}
          </h2>
          <div class="text-sm mt-1">
            <span class="font-medium">{{ translationService.translate('lineNumberText') }}</span>
            @if(addingLineToExistingWo){
            <span class="ml-2">{{ currentLineNo }}</span>
            } @else{
            <span class="ml-2">{{ isEditMode ? editingLine?.lineNumber : getNextLineNumber() }}</span>
            }
          </div>
        </div>
        <div class="flex items-center gap-2">
          @if (lineCamRejected) {
          <mic-status class="error whitespace-nowrap">&#9888; {{ translationService.translate('rejectedText') }}</mic-status>
          }
          <mic-status class="primary whitespace-nowrap">* {{ translationService.translate('requiredFieldsText') }}</mic-status>
        </div>
      </div>
    </div>

    <div class="form-grid form-grid-3">
      <mat-divider class="col-span-1 sm-col-span-1 md-col-span-2 lg-col-span-3 xl-col-span-3"></mat-divider>

      <!-- Row 1: Barcode, Production Customer, Process Code, Work Type -->
      <div class="col-span-3 grid grid-cols-1 sm-grid-cols-1 md-grid-cols-2 lg-grid-cols-5 xl-grid-cols-5 gap-3 items-start">
        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full field-height">
            <mat-label>{{ translationService.translate('barCodeNumberText') }}</mat-label>
            <input
              matInput
              formControlName="barcode"
              autocomplete="off"
              maxlength="10"
              style="text-transform: uppercase"
              (blur)="onBarcodeBlur()"
            />
            @if (barcodeValidationLoading) {
            <div matSuffix class="flex items-center">
              <mat-spinner diameter="16"></mat-spinner>
            </div>
            } @if (submittedLine && lineWOForm.get('barcode')?.hasError('required')) {
            <mat-error>{{ translationService.translate('mandatoryFieldText') }}</mat-error>
            } @if ((submittedLine || lineWOForm.get('barcode')?.touched) && lineWOForm.get('barcode')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max10CharactersText') }}</mat-error>
            } @if ((submittedLine || lineWOForm.get('barcode')?.touched) && lineWOForm.get('barcode')?.hasError('barcode')) {
            <mat-error>{{ translationService.translate('onlyLettersAndNumbersText') }}</mat-error>
            } @if (lineWOForm.get('barcode')?.hasError('barcodeExistsLocal')) {
            <mat-error>{{ translationService.translate('barcodeAlreadyAddedText') }}</mat-error>
            } @if (lineWOForm.get('barcode')?.hasError('barcodeExists')) {
            <mat-error>{{ translationService.translate('barcodeExistsSystemText') }}</mat-error>
            } @if (lineWOForm.get('barcode')?.hasError('barcodeNotIncrementable')) {
            <mat-error>{{ translationService.translate('barcodeNotIncrementableText') }}</mat-error>
            }
          </mat-form-field>
        </div>

        <div class="grid-item col-span-1 sm-col-span-1 md-col-span-1 lg-col-span-2 xl-col-span-2">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('productionCustomerText') }}"
            requiredLabel="{{ translationService.translate('mandatoryFieldText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{ translationService.translate('selectText') }} {{ translationService.translate('productionCustomerText') }}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            [formControlName]="'productionCustomerId'"
            [data]="productionCustomers"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>

        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('casingProcessCodeText') }}"
            requiredLabel="{{ translationService.translate('mandatoryFieldText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{ `${translationService.translate('selectText')} ${translationService.translate('casingProcessCodeText')}`}}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            [formControlName]="'processCodeId'"
            [data]="lineProcessCodes"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>

        <div class="grid-item">
          <mat-card class="custom-card-no-shadow view-component">
            <mat-form-field subscriptSizing="dynamic" class="grid-item readonly-field">
              <mat-label>{{ translationService.translate('workTypeText') }}</mat-label>
              <input matInput type="text" formControlName="workType" autocomplete="off" readonly tabindex="-1" />
            </mat-form-field>
          </mat-card>
        </div>
      </div>

      <mat-divider class="col-span-1 sm-col-span-1 md-col-span-2 lg-col-span-3 xl-col-span-3"></mat-divider>

      <!-- Row 2: Make, Series, Size, Mold (conditional), Tread, Length (conditional) -->
      <div
        class="col-span-3 grid grid-cols-1 sm-grid-cols-1 md-grid-cols-2 gap-3 items-start"
        [ngClass]="showMoldRadio ? 'lg-grid-cols-3 xl-grid-cols-3' : 'lg-grid-cols-4 xl-grid-cols-4'"
      >
        <div class="grid-item">
          <div class="flex items-center">
            <mic-dropdown-search
              micDropdownAccessibility
              class="flex-1"
              [widthConfig]="{ width: '100%' }"
              label="{{ translationService.translate('makeText') }}"
              requiredLabel="{{ translationService.translate('mandatoryFieldText') }}"
              nullLabel="{{ translationService.translate('noneText') }}"
              placeholder="{{ translationService.translate('selectMakeText') }}"
              emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
              searchPlaceholder="{{ translationService.translate('SearchText') }}"
              subscriptSizing="dynamic"
              [formControlName]="'makeId'"
              [data]="makes"
              [attribute]="'label'"
              [compareWith]="compareById"
            >
            </mic-dropdown-search>
            @if (makesLoading) {
            <span class="inline-flex items-center ml-2">
              <mat-spinner diameter="16"></mat-spinner>
            </span>
            }
          </div>
        </div>

        <div class="grid-item">
          <div class="flex items-center">
            <mic-dropdown-search
              micDropdownAccessibility
              class="flex-1"
              [widthConfig]="{ width: '100%' }"
              label="{{ translationService.translate('seriesText') }}"
              requiredLabel="{{ translationService.translate('mandatoryFieldText') }}"
              nullLabel="{{ translationService.translate('noneText') }}"
              placeholder="{{ translationService.translate('selectSeriesText') }}"
              emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
              searchPlaceholder="{{ translationService.translate('SearchText') }}"
              subscriptSizing="dynamic"
              [formControlName]="'seriesId'"
              [data]="series"
              [attribute]="'label'"
              [compareWith]="compareById"
            >
            </mic-dropdown-search>
            @if (seriesLoading) {
            <span class="inline-flex items-center ml-2">
              <mat-spinner diameter="16"></mat-spinner>
            </span>
            }
          </div>
        </div>

        <div class="grid-item">
          <div class="flex items-center">
            <mic-dropdown-search
              micDropdownAccessibility
              class="flex-1"
              [widthConfig]="{ width: '100%' }"
              label="{{ translationService.translate('sizeText') }}"
              requiredLabel="{{ translationService.translate('mandatoryFieldText') }}"
              nullLabel="{{ translationService.translate('noneText') }}"
              placeholder="{{ translationService.translate('selectSizeText') }}"
              emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
              searchPlaceholder="{{ translationService.translate('SearchText') }}"
              subscriptSizing="dynamic"
              [formControlName]="'sizeId'"
              [data]="sizes"
              [attribute]="'label'"
              [compareWith]="compareById"
            >
            </mic-dropdown-search>
            @if (sizesLoading) {
            <span class="inline-flex items-center ml-2">
              <mat-spinner diameter="16"></mat-spinner>
            </span>
            }
          </div>
        </div>

        @if (showMoldRadio) {
        <div class="grid-item col-span-1">
          <div class="cure-type-wrapper">
            <div class="cure-type-outline" [class.cure-type-error]="submittedLine && lineWOForm.get('moldType')?.invalid">
              <span class="cure-type-label" [class.required-label]="lineWOForm.get('moldType')?.hasValidator(validators.required)">{{
                translationService.translate('cureTypeText')
              }}</span>
              <mat-radio-group formControlName="moldType" class="mic-radio-group">
                <mat-radio-button value="P" (keydown.enter)="onMoldTypeEnterKey('P', $event)">{{
                  translationService.translate('hyphenPreMoldText')
                }}</mat-radio-button>
                <mat-radio-button value="M" (keydown.enter)="onMoldTypeEnterKey('M', $event)">{{
                  translationService.translate('customMoldText')
                }}</mat-radio-button>
              </mat-radio-group>
            </div>
            @if (submittedLine && lineWOForm.get('moldType')?.hasError('required')) {
            <mat-error class="cure-type-subscript-error">{{ translationService.translate('mandatoryFieldText') }}</mat-error>
            }
          </div>
        </div>
        }

        <div class="grid-item">
          <div class="flex items-center">
            <mic-dropdown-search
              micDropdownAccessibility
              class="flex-1"
              [required]="treadRequired"
              [widthConfig]="{ width: '100%' }"
              label="{{ translationService.translate('treadDesignText') }}"
              [requiredLabel]="treadRequired ? translationService.translate('mandatoryFieldText') : null"
              nullLabel="{{ translationService.translate('noneText') }}"
              placeholder="{{ translationService.translate('selectTreadText') }}"
              emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
              searchPlaceholder="{{ translationService.translate('SearchText') }}"
              subscriptSizing="dynamic"
              [formControlName]="'treadId'"
              [data]="treads"
              [attribute]="'label'"
              [compareWith]="compareById"
            >
            </mic-dropdown-search>
            @if (treadsLoading) {
            <span class="inline-flex items-center ml-2">
              <mat-spinner diameter="16"></mat-spinner>
            </span>
            }
          </div>
        </div>

        @if (headerSettings?.lengthFlag !== 'N' && headerSettings?.lengthFlag) {
        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('lengthText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{ `${translationService.translate('selectText')} ${translationService.translate('treadLengthText')}`}}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            [formControlName]="'lengthId'"
            [data]="lengthOptions"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>
        }
      </div>

      <mat-divider class="col-span-1 sm-col-span-1 md-col-span-2 lg-col-span-3 xl-col-span-3"></mat-divider>

      <!-- Row 3: NTR (cond), Application, Axle, MFG DOT, RT DOT (cond), CAM Spec -->
      <div class="col-span-3 grid grid-cols-1 sm-grid-cols-1 md-grid-cols-2 lg-grid-cols-3 xl-grid-cols-3 gap-3 items-start">
        @if (headerSettings?.ntrFlag !== 'N' && headerSettings?.ntrFlag) {
        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full field-height">
            <mat-label>{{ translationService.translate('numberOfTimesRetreadedText') }}</mat-label>
            <input
              matInput
              formControlName="ntrId"
              placeholder="{{`${translationService.translate('enterMfgDotText').split(' ')[0]} ${translationService.translate('ntrText')}`}}"
              maxlength="1"
              autocomplete="off"
            />
            @if (lineNtrCamValidating) {
            <mat-spinner matSuffix diameter="16"></mat-spinner>
            } @if (submittedLine && lineWOForm.get('ntrId')?.hasError('required')) {
            <mat-error>{{ translationService.translate('mandatoryFieldText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('ntrId')?.hasError('ntrDigit')) {
            <mat-error>{{ translationService.translate('ntrDigitErrorText') }}</mat-error>
            }
          </mat-form-field>
          @if (pendingCamViolation?.type === 'ntr') {
          <div class="cam-violation-banner">
            <div class="cam-violation-message">
              <span class="cam-warning-icon">&#9888;</span>
              <span
                >{{ translationService.translate('ntrText') }} {{ translationService.translate('valueText') }}
                <strong>{{ pendingCamViolation!.value }}</strong> {{ translationService.translate('exceedsCamSpecLimitText') }}
                <strong>{{ pendingCamViolation!.limit }}</strong
                >. {{ translationService.translate('chooseActionProceedText') }}</span
              >
            </div>
            <div class="cam-violation-actions">
              <button mat-stroked-button type="button" color="primary" (click)="onCamFix()">
                {{ translationService.translate('fixText') }}
              </button>
              @if (headerSettings?.canIgnoreFleetSpecs) {
              <button mat-stroked-button type="button" (click)="onCamIgnore()">
                {{ translationService.translate('actionIgnoreText').split(' ')[0] }}
              </button>
              } @if(pendingCamViolation!.action?.toUpperCase() !== 'DOWNGRADE'){
              <button mat-flat-button type="button" color="warn" (click)="onCamReject()">
                {{ translationService.translate('rejectCasingText') }}
              </button>
              }
            </div>
            @if (pendingCamViolation!.action?.toUpperCase() === 'DOWNGRADE' && camDowngradeTreads.length > 0) {
            <div class="cam-downgrade-section">
              @if (pendingCamViolation!.application) {
              <div class="cam-downgrade-info">
                <span class="cam-downgrade-label">{{ translationService.translate('applicationText') }}</span>
                <span class="cam-downgrade-value">{{ pendingCamViolation!.application }}</span>
              </div>
              } @if (pendingCamViolation!.axle) {
              <div class="cam-downgrade-info">
                <span class="cam-downgrade-label">{{ translationService.translate('axleText') }}</span>
                <span class="cam-downgrade-value">{{ pendingCamViolation!.axle }}</span>
              </div>
              }
              <div class="cam-downgrade-tread-row">
                <mat-form-field subscriptSizing="dynamic" class="cam-downgrade-tread-field">
                  <mat-label>{{ translationService.translate('selectDownGradeOptionsText') }}</mat-label>
                  <mat-select [value]="selectedDowngradeTread" (selectionChange)="selectedDowngradeTread = $event.value">
                    @for (tread of camDowngradeTreads; track tread.id) {
                    <mat-option [value]="tread">{{ tread.value }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <button mat-flat-button type="button" color="primary" [disabled]="!selectedDowngradeTread" (click)="onCamDowngrade()">
                  {{ translationService.translate('actionDowngradeText') }}
                </button>
              </div>
            </div>
            }
          </div>
          }
        </div>
        }

        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('applicationText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{ translationService.translate('selectApplicationText') }}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            [formControlName]="'application'"
            [data]="applicationOptions"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>

        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('axleText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{ translationService.translate('selectAxleText') }}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            [formControlName]="'axle'"
            [data]="axleOptions"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>

        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full field-height">
            <mat-label>{{ translationService.translate('mfgDotText') }}</mat-label>
            <input matInput formControlName="mfgDot" maxlength="4" />
            @if (lineMdotCamValidating) {
            <mat-spinner matSuffix diameter="16"></mat-spinner>
            } @if (submittedLine && lineWOForm.get('mfgDot')?.hasError('required')) {
            <mat-error>{{ translationService.translate('mandatoryFieldText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('mfgDot')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max4CharactersText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('mfgDot')?.hasError('alnum')) {
            <mat-error>{{ translationService.translate('lettersNumbersOnlyText') }}</mat-error>
            } @if (lineWOForm.get('mfgDot')?.hasError('mfgDotFormat')) {
            <mat-error>{{ lineWOForm.get('mfgDot')?.getError('mfgDotFormat')?.hint }}</mat-error>
            }
          </mat-form-field>
          @if (pendingCamViolation?.type === 'age') {
          <div class="cam-violation-banner">
            <div class="cam-violation-message">
              <span class="cam-warning-icon">&#9888;</span>
              <span
                >{{ translationService.translate('mdotText') }} {{ translationService.translate('ageMonthsText').split('(')[0] }}
                <strong>{{ pendingCamViolation!.value }}</strong> {{ translationService.translate('monthsText') }}
                {{ translationService.translate('exceedsCamSpecLimitText') }} <strong>{{ pendingCamViolation!.limit }}</strong>
                {{ translationService.translate('monthsText') }}. {{ translationService.translate('chooseActionProceedText') }}</span
              >
            </div>
            <div class="cam-violation-actions">
              <button mat-stroked-button type="button" color="primary" (click)="onCamFix()">
                {{ translationService.translate('fixText') }}
              </button>
              @if (headerSettings?.canIgnoreFleetSpecs) {
              <button mat-stroked-button type="button" (click)="onCamIgnore()">
                {{ translationService.translate('actionIgnoreText').split(' ')[0] }}
              </button>
              } @if(pendingCamViolation!.action?.toUpperCase() !== 'DOWNGRADE'){
              <button mat-flat-button type="button" color="warn" (click)="onCamReject()">
                {{ translationService.translate('rejectCasingText') }}
              </button>
              }
            </div>
            @if (pendingCamViolation!.action?.toUpperCase() === 'DOWNGRADE' && camDowngradeTreads.length > 0) {
            <div class="cam-downgrade-section">
              @if (pendingCamViolation!.application) {
              <div class="cam-downgrade-info">
                <span class="cam-downgrade-label">{{ translationService.translate('applicationText') }}</span>
                <span class="cam-downgrade-value">{{ pendingCamViolation!.application }}</span>
              </div>
              } @if (pendingCamViolation!.axle) {
              <div class="cam-downgrade-info">
                <span class="cam-downgrade-label">{{ translationService.translate('axleText') }}</span>
                <span class="cam-downgrade-value">{{ pendingCamViolation!.axle }}</span>
              </div>
              }
              <div class="cam-downgrade-tread-row">
                <mat-form-field subscriptSizing="dynamic" class="cam-downgrade-tread-field">
                  <mat-label>{{ translationService.translate('selectDownGradeOptionsText') }}</mat-label>
                  <mat-select [value]="selectedDowngradeTread" (selectionChange)="selectedDowngradeTread = $event.value">
                    @for (tread of camDowngradeTreads; track tread.id) {
                    <mat-option [value]="tread">{{ tread.value }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <button mat-flat-button type="button" color="primary" [disabled]="!selectedDowngradeTread" (click)="onCamDowngrade()">
                  {{ translationService.translate('actionDowngradeText') }}
                </button>
              </div>
            </div>
            }
          </div>
          }
        </div>

        @if (customerRtDot !== 'N') {
        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full field-height">
            <mat-label>{{ translationService.translate('rtDotLabelText') }}</mat-label>
            <input
              matInput
              formControlName="rtDot"
              placeholder="{{`${translationService.translate('enterMfgDotText').split(' ')[0]} ${translationService.translate('rtDotLabelText')}`}}"
              maxlength="7"
            />
            @if (rtDotValidating) {
            <mat-spinner matSuffix diameter="16"></mat-spinner>
            } @if (submittedLine && lineWOForm.get('rtDot')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max7CharactersText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('rtDot')?.hasError('alnum')) {
            <mat-error>{{ translationService.translate('lettersNumbersOnlyText') }}</mat-error>
            } @if (lineWOForm.get('rtDot')?.hasError('rtDotInvalid')) {
            <mat-error>{{ lineWOForm.get('rtDot')?.getError('rtDotInvalid')?.message }}</mat-error>
            } @if (lineWOForm.get('rtDot')?.hasError('rtDotRequiredForReject')) {
            <mat-error>{{ translationService.translate('mandatoryFieldText') }}</mat-error>
            }
          </mat-form-field>
        </div>
        }

        <div class="grid-item">
          <mat-card class="custom-card-no-shadow view-component">
            <mat-form-field subscriptSizing="dynamic" class="grid-item readonly-field">
              <mat-label>{{ translationService.translate('camSpecIdText') }}</mat-label>
              <input matInput type="text" formControlName="camSpec" autocomplete="off" readonly tabindex="-1" />
            </mat-form-field>
          </mat-card>
        </div>
      </div>

      <mat-divider class="col-span-1 sm-col-span-1 md-col-span-2 lg-col-span-3 xl-col-span-3"></mat-divider>

      <!-- Row 4: Brand No, Grade, Override Grade, Tread Depth (cond), Wheel Type, Wheel Color, Casing Value (cond) -->
      <div class="col-span-3 grid grid-cols-1 sm-grid-cols-1 md-grid-cols-2 lg-grid-cols-3 xl-grid-cols-3 gap-3 items-start">
        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full field-height">
            <mat-label>{{ translationService.translate('brandText') }}</mat-label>
            <input
              matInput
              formControlName="brandNumber"
              placeholder="{{ translationService.translate('enterBrandNumberText') }}"
              maxlength="25"
              (input)="onBrandNumberInput($event)"
            />
            @if (submittedLine && lineWOForm.get('brandNumber')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max25CharactersText') }}</mat-error>
            }
          </mat-form-field>
        </div>

        <div class="grid-item">
          <mat-card class="custom-card-no-shadow view-component">
            <mat-form-field subscriptSizing="dynamic" class="grid-item readonly-field">
              <mat-label>{{ translationService.translate('gradeText') }}</mat-label>
              <input
                matInput
                placeholder="{{ translationService.translate('basedOnCasingSpecsText') }}"
                formControlName="systemGrade"
                maxlength="4"
                readonly
                tabindex="-1"
              />
            </mat-form-field>
          </mat-card>
        </div>

        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('overRideGradeText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{ `${translationService.translate('selectText')} ${translationService.translate('overRideGradeText')}`}}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            formControlName="overrideGrade"
            [data]="casingGradeOptions"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>

        @if (showTreadDepthFields) {
        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full field-height">
            <mat-label>{{ translationService.translate('treadDepthMinText') }}</mat-label>
            <input
              matInput
              formControlName="treadDepthMin"
              placeholder="{{ translationService.translate('enterTreadDepthMinText') }}"
              maxlength="2"
            />
            @if (submittedLine && lineWOForm.get('treadDepthMin')?.hasError('required')) {
            <mat-error>{{ translationService.translate('mandatoryFieldText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('treadDepthMin')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max2CharactersText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('treadDepthMin')?.hasError('num')) {
            <mat-error>{{ translationService.translate('numbersOnlyText') }}</mat-error>
            }
          </mat-form-field>
        </div>
        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full field-height">
            <mat-label>{{ translationService.translate('treadDepthMaxText') }}</mat-label>
            <input
              matInput
              formControlName="treadDepthMax"
              placeholder="{{ translationService.translate('enterTreadDepthMaxText') }}"
              maxlength="2"
            />
            @if (submittedLine && lineWOForm.get('treadDepthMax')?.hasError('required')) {
            <mat-error>{{ translationService.translate('mandatoryFieldText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('treadDepthMax')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max2CharactersText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('treadDepthMax')?.hasError('num')) {
            <mat-error>{{ translationService.translate('numbersOnlyText') }}</mat-error>
            }
          </mat-form-field>
        </div>
        }

        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('wheelTypeText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{ `${translationService.translate('selectText')} ${translationService.translate('wheelTypeText')}`}}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            formControlName="wheelType"
            [data]="wheelOptions"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>

        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('wheelColorText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{ `${translationService.translate('selectText')} ${translationService.translate('wheelColorText')}`}}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            formControlName="wheelColor"
            [data]="wheelColorOptions"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>

        @if (showCasingValueField) {
        <div class="grid-item">
          <mat-form-field subscriptSizing="dynamic" class="w-full field-height">
            <mat-label>{{ translationService.translate('casingValueText') }}</mat-label>
            <input matInput formControlName="casingValue" maxlength="9" (blur)="formatCasingValue()" />
            @if (submittedLine && lineWOForm.get('casingValue')?.hasError('required')) {
            <mat-error>{{ translationService.translate('mandatoryFieldText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('casingValue')?.hasError('maxlength')) {
            <mat-error>{{ translationService.translate('max9CharactersText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('casingValue')?.hasError('number')) {
            <mat-error>{{ translationService.translate('numbersDecimalAllowedText') }}</mat-error>
            } @if (submittedLine && lineWOForm.get('casingValue')?.hasError('min')) {
            <mat-error>{{ translationService.translate('minimum0Text') }}</mat-error>
            }
          </mat-form-field>
        </div>
        }
      </div>

      <mat-divider class="col-span-1 sm-col-span-1 md-col-span-2 lg-col-span-3 xl-col-span-3"></mat-divider>

      <!-- Row 5: SC Code, NP Code, Disposal Charge (conditional on process code) -->
      @if (lineScProcessCode || lineNpProcessCode) {
      <div class="col-span-3 grid grid-cols-1 sm-grid-cols-1 md-grid-cols-2 lg-grid-cols-3 xl-grid-cols-3 gap-3 items-start">
        @if (lineScProcessCode) {
        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('scrapCodeText') }}"
            requiredLabel="{{ translationService.translate('mandatoryFieldText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{`${translationService.translate('selectText')} ${translationService.translate('scrapCodeText')}`}}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            formControlName="scrapCode"
            [data]="rejectReasonOptions"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>
        } @if (lineNpProcessCode) {
        <div class="grid-item">
          <mic-dropdown-search
            micDropdownAccessibility
            class="flex-1"
            [widthConfig]="{ width: '100%' }"
            label="{{ translationService.translate('npCodeText') }}"
            requiredLabel="{{ translationService.translate('mandatoryFieldText') }}"
            nullLabel="{{ translationService.translate('noneText') }}"
            placeholder="{{`${translationService.translate('selectText')} ${translationService.translate('npCodeText')}`}}"
            emptyPlaceholder="{{ translationService.translate('noDataFoundText') }}"
            searchPlaceholder="{{ translationService.translate('SearchText') }}"
            subscriptSizing="dynamic"
            formControlName="npCode"
            [data]="rejectReasonOptions"
            [attribute]="'label'"
            [compareWith]="compareById"
          >
          </mic-dropdown-search>
        </div>
        } @if (lineScProcessCode) {
        <div class="grid-item">
          <mat-card class="custom-card-no-shadow view-component">
            <mat-form-field subscriptSizing="dynamic" class="grid-item readonly-field">
              <mat-label>{{ translationService.translate('disposalChargeDescriptionText') }}</mat-label>
              <input matInput readonly tabindex="-1" [value]="lineWOForm.get('disposalCharge')?.value?.description || ''" />
            </mat-form-field>
          </mat-card>
        </div>
        }
      </div>
      <mat-divider class="col-span-1 sm-col-span-1 md-col-span-2 lg-col-span-3 xl-col-span-3"></mat-divider>
      }

      <!-- Casing Notes -->
      <div class="grid-item col-span-3">
        <mat-form-field subscriptSizing="dynamic" floatLabel="always" class="w-full">
          <mat-label>{{ translationService.translate('casingNotesText') }}</mat-label>
          <textarea matInput rows="2" maxlength="70" formControlName="casingNotes"></textarea>
          @if (submittedLine && lineWOForm.get('casingNotes')?.hasError('maxlength')) {
          <mat-error>{{ translationService.translate('max70CharactersText') }}</mat-error>
          }
        </mat-form-field>
      </div>

      <!-- Edit-mode validation warnings: shown when a header field change (plant / customer)
           has made a previously stored value unavailable. Fields are cleared so form
           validation will block save until the user re-selects a valid value. -->
      @if (lineErrors.length > 0) {
      <div class="col-span-1 sm-col-span-1 md-col-span-2 lg-col-span-3 xl-col-span-3">
        @for (err of lineErrors; track err) {
        <div class="cam-warning mb-1">
          <span class="cam-warning-icon">&#9888;</span>
          <span>{{ err }}</span>
        </div>
        }
      </div>
      }

      <mat-divider class="col-span-1 sm-col-span-1 md-col-span-2 lg-col-span-3 xl-col-span-3"></mat-divider>

      <!-- Action Buttons -->
      <div class="col-span-3 flex justify-end items-center">
        <div class="flex flex-row gap-2">
          <button mat-stroked-button class="w-auto" color="primary" type="button" (click)="onCancelLine()">
            {{ translationService.translate('cancelText') }}
          </button>
          @if (!isEditMode) {
          <button mat-flat-button class="w-auto" color="primary" type="button" [disabled]="isLineAddDisabled" (click)="addWorkOrderLine()">
            @if(addingLineToExistingWo){
            {{`${translationService.translate('saveLinkText')} & ${translationService.translate('addAnotherText')}`}}
            }@else{
            {{ translationService.translate('addAnotherText') }}
            }
          </button>
          <button mat-flat-button class="w-auto" color="primary" type="button" [disabled]="isLineAddDisabled" (click)="onAddLineAndClose()">
            @if(addingLineToExistingWo){
            {{`${translationService.translate('saveLinkText')} & ${translationService.translate('closeText')}`}}
            }@else{
            {{`${translationService.translate('addText')} & ${translationService.translate('closeText')}`}}
            }
          </button>
          } @if (isEditMode) {
          <button mat-flat-button class="w-auto" color="primary" type="button" [disabled]="isLineAddDisabled" (click)="onAddLineAndClose()">
            {{`${translationService.translate('updateText')} & ${translationService.translate('closeText')}`}}
          </button>
          }
        </div>
      </div>
    </div>
  </form>
</ng-template>
